import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { X, Bug, Lightbulb, Send, Loader2, CheckCircle2, Clock, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  open: { label: "Open", cls: "border-neutral-400 text-neutral-700" },
  in_progress: { label: "In behandeling", cls: "border-blue-500 text-blue-700" },
  resolved: { label: "Opgelost", cls: "border-emerald-600 text-emerald-700" },
  closed: { label: "Gesloten", cls: "border-neutral-300 text-neutral-500" },
};

export default function TicketModal({ onClose }) {
  const { t } = useT();
  const [kind, setKind] = useState("bug");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tickets");
      setHistory(data || []);
    } catch {
      // silent — history is optional
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      toast.error(t("ticketRequired"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/tickets", { kind, subject: subject.trim(), description: description.trim() });
      toast.success(t("ticketSent"));
      setSubject("");
      setDescription("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("ticketFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 lg:left-72 z-[60] flex items-center justify-center p-4" data-testid="ticket-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-black/10 w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <p className="ed-eyebrow">{t("ticketEyebrow")}</p>
            <h2 className="font-serif text-xl mt-0.5">{t("ticketTitle")}</h2>
          </div>
          <button data-testid="ticket-close" onClick={onClose} className="p-2 border border-neutral-300 hover:border-neutral-900">
            <X size={13} strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <p className="ed-label mb-2">{t("ticketKindLabel")}</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button" data-testid="ticket-kind-bug"
                onClick={() => setKind("bug")}
                className={`flex items-center justify-center gap-2 border px-4 py-3 font-mono text-xs uppercase tracking-[0.12em] transition-colors ${kind === "bug" ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 hover:border-neutral-900"}`}
              >
                <Bug size={13} strokeWidth={1.5} /> {t("ticketKindBug")}
              </button>
              <button
                type="button" data-testid="ticket-kind-suggestion"
                onClick={() => setKind("suggestion")}
                className={`flex items-center justify-center gap-2 border px-4 py-3 font-mono text-xs uppercase tracking-[0.12em] transition-colors ${kind === "suggestion" ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 hover:border-neutral-900"}`}
              >
                <Lightbulb size={13} strokeWidth={1.5} /> {t("ticketKindSuggestion")}
              </button>
            </div>
          </div>

          <label className="block">
            <span className="ed-label mb-1 block">{t("ticketSubjectLabel")}</span>
            <input
              data-testid="ticket-subject"
              className="ed-input"
              maxLength={140}
              placeholder={kind === "bug" ? t("ticketSubjectBugPlaceholder") : t("ticketSubjectSuggestionPlaceholder")}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </label>

          <label className="block">
            <span className="ed-label mb-1 block">{t("ticketDescriptionLabel")}</span>
            <textarea
              data-testid="ticket-description"
              className="ed-input min-h-[7rem]"
              maxLength={4000}
              placeholder={kind === "bug" ? t("ticketDescriptionBugPlaceholder") : t("ticketDescriptionSuggestionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
            <span className="text-[0.6rem] font-mono text-neutral-500">{description.length}/4000</span>
          </label>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 underline underline-offset-2">
              {t("ticketCancel")}
            </button>
            <button
              data-testid="ticket-submit" type="submit" disabled={busy || !subject.trim() || !description.trim()}
              className="inline-flex items-center gap-2 bg-neutral-900 text-[#F9F8F6] px-5 py-2.5 font-mono text-[0.65rem] uppercase tracking-[0.15em] hover:bg-neutral-700 transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} strokeWidth={1.5} />}
              {t("ticketSubmit")}
            </button>
          </div>
        </form>

        {/* Prior tickets — small history block */}
        <div className="px-6 py-4 border-t border-neutral-200">
          <p className="ed-label mb-3 flex items-center gap-2"><MessageSquare size={12} strokeWidth={1.5} /> {t("ticketHistory")}</p>
          {loadingHistory ? (
            <p className="text-[0.7rem] font-mono text-neutral-500">…</p>
          ) : history.length === 0 ? (
            <p className="text-[0.7rem] font-mono text-neutral-500 italic">{t("ticketHistoryEmpty")}</p>
          ) : (
            <ul className="space-y-2" data-testid="ticket-history-list">
              {history.slice(0, 5).map((tk) => (
                <li key={tk.id} data-testid={`ticket-history-${tk.id}`} className="border border-neutral-200 bg-[#FAFAF8] p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {tk.kind === "bug" ? <Bug size={12} strokeWidth={1.5} className="shrink-0" /> : <Lightbulb size={12} strokeWidth={1.5} className="shrink-0" />}
                      <span className="font-serif text-sm truncate">{tk.subject}</span>
                    </div>
                    <span className={`font-mono text-[0.55rem] uppercase tracking-[0.12em] px-2 py-1 border shrink-0 ${STATUS_META[tk.status]?.cls || ""}`}>
                      {STATUS_META[tk.status]?.label || tk.status}
                    </span>
                  </div>
                  {tk.admin_reply && (
                    <div className="mt-2 text-xs bg-white border border-neutral-200 p-2 leading-relaxed">
                      <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-emerald-700 mb-1 flex items-center gap-1">
                        <CheckCircle2 size={10} strokeWidth={2} /> {t("ticketAdminReply")}
                      </p>
                      <p className="whitespace-pre-wrap text-neutral-700">{tk.admin_reply}</p>
                    </div>
                  )}
                  <p className="mt-1 font-mono text-[0.55rem] text-neutral-500 flex items-center gap-1">
                    <Clock size={9} strokeWidth={1.5} /> {new Date(tk.created_at).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
